import type { DataVizBlockData, ResolvedChartSeries } from '@agavi/blocks';

/** The part of a series these helpers actually read. Authored and resolved series differ only in
 *  `y`, so anything that looks at `x` alone works on both and should say so in its own signature
 *  rather than force a caller to resolve data it is not going to draw. */
type XOnlySeries = { points: { x: number | string }[] };

/**
 * Validated categorical palette (dataviz skill reference instance), defined as CSS custom
 * properties in globals.css (`--ws-chart-series-1..8`, light in `:root` / dark in `.dark`) rather
 * than computed here — referencing the CSS variable lets the app's existing `.dark` class-based
 * theming (see useDarkMode) repaint the series color for free on toggle, with no JS re-render
 * needed. Fixed slot order is the CVD-safety mechanism, never cycled per-series beyond 8 — a 9th+
 * series reuses the last slot rather than generating a new hue.
 */
const PALETTE_SIZE = 8;

export function defaultSeriesColor(index: number): string {
  const slot = Math.min(index, PALETTE_SIZE - 1) + 1;
  // Referencing the raw --ws-* token directly, not the --color-ws-* alias under @theme inline —
  // Tailwind v4 only emits that alias into the compiled stylesheet when a matching utility class
  // (e.g. bg-ws-chart-series-1) is actually used somewhere; since this token is only ever consumed
  // via a raw var() in an SVG attribute (never as a class name), the alias would silently resolve
  // to nothing. The underlying --ws-* custom property is declared directly in a plain `:root`/`.dark`
  // rule (not JIT-gated), so it's always present regardless of what Tailwind's scanner sees.
  return `var(--ws-chart-series-${slot})`;
}

/**
 * Distinct marker shapes reinforce color as a second identity channel for overlapping series
 * (Recharts' own `Symbols` component renders all four natively, and its `legendType` prop accepts
 * the same string, so the legend icon matches the on-chart marker for free). Fixed order, same
 * "never cycled independently" spirit as the color palette — but since color already stays unique
 * up to 8 series, shape is only a reinforcing channel, so a 5th+ series safely reuses shape 1 (it
 * still has a distinct color).
 */
const MARKER_SHAPES = ['circle', 'square', 'triangle', 'diamond'] as const;
export type MarkerShape = (typeof MARKER_SHAPES)[number];

export function defaultSeriesShape(index: number): MarkerShape {
  return MARKER_SHAPES[index % MARKER_SHAPES.length]!;
}

/**
 * What to actually show for a series' name, without ever writing a fallback into stored data —
 * `series.label` stays exactly what the teacher typed (or '' if they never touched it). With a
 * single series, an untouched label falls back to the y-axis label (preserves the block's old
 * single-series behavior, where there was no separate "series name" concept); with 2+ series that
 * fallback would be misleading (yAxisLabel is a shared axis title, not any one series' name), so it
 * falls back to a generic "Series N" instead.
 */
export function seriesDisplayLabel(
  /* Only the label, so it takes only the label — the editing grid calls this with an AUTHORED
     series (whose `y` may be a variable reference) and the chart with a resolved one. */
  series: Pick<ResolvedChartSeries, 'label'>,
  index: number,
  totalSeries: number,
  yAxisLabel: DataVizBlockData['yAxisLabel'],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (series.label.trim()) return series.label;
  if (totalSeries === 1 && yAxisLabel?.trim()) return yAxisLabel;
  return t('blocks.dataviz.grid.defaultSeriesLabel', { n: index + 1 });
}

/** `x` only, so either shape answers it — `y` is what resolution changes and nothing here reads it. */
export function isNumericXAxis(series: XOnlySeries[]): boolean {
  return series.every((s) => s.points.every((p) => typeof p.x === 'number'));
}

/** All distinct x values across every series — numerically sorted if every point's x is a number, else first-seen order. */
export function collectXValues(series: XOnlySeries[]): Array<number | string> {
  const seen = new Set<number | string>();
  const order: Array<number | string> = [];
  for (const s of series) {
    for (const p of s.points) {
      if (!seen.has(p.x)) {
        seen.add(p.x);
        order.push(p.x);
      }
    }
  }
  if (isNumericXAxis(series)) {
    return [...order].sort((a, b) => (a as number) - (b as number));
  }
  return order;
}

export interface RechartsRow {
  x: number | string;
  [seriesId: string]: number | string | undefined;
}

/** Reshapes sparse, independent per-series point arrays into Recharts' shared row-array-keyed-by-x shape. */
export function toRechartsRows(series: ResolvedChartSeries[]): RechartsRow[] {
  const xValues = collectXValues(series);
  const pointMaps = series.map((s) => new Map(s.points.map((p) => [p.x, p.y])));
  return xValues.map((x) => {
    const row: RechartsRow = { x };
    series.forEach((s, i) => {
      row[s.id] = pointMaps[i]!.get(x);
    });
    return row;
  });
}

/** Matches a bare numeric-looking string (currency/percent/comma/paren-negative already stripped
 * down to digits, or still wearing those decorations) — used to keep `parseDateValue` from ever
 * treating a plain number, currency amount, or percentage as a date via `Date.parse`'s permissive
 * built-in coercion. */
const NUMERIC_LOOKING_PATTERN = /^[+-]?[\d.,\s]+%?$/;

/**
 * Real-world spreadsheet exports rarely give you a bare number — currency columns come as
 * `"$1,000.00 "` (symbol, thousands separator, trailing space), percentage columns as `"25%"`,
 * and accounting exports use parenthesized negatives like `"(1,234.56)"`. Plain `Number(raw)`
 * returns NaN for all of these, which — before this fix — meant every row silently failed to
 * parse and the import proceeded anyway with zero points, replacing existing data with nothing.
 */
export function parseNumericValue(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return NaN;
  const isParenNegative = /^\(.*\)$/.test(trimmed);
  const stripped = trimmed.replace(/^\(|\)$/g, '').replace(/[$€£¥%,\s]/g, '');
  if (stripped === '') return NaN;
  const n = Number(stripped);
  if (isNaN(n)) return NaN;
  return isParenNegative ? -n : n;
}

/**
 * Parses a real calendar date string into epoch ms, or `null` if `raw` isn't a recognizable date.
 * Bare numeric-looking strings (plain numbers, currency, percentages, Excel serial numbers) are
 * rejected up front rather than handed to `Date.parse` — its built-in coercion is permissive
 * enough to otherwise misread e.g. a raw number as a date.
 */
export function parseDateValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || NUMERIC_LOOKING_PATTERN.test(trimmed)) return null;
  const ms = Date.parse(trimmed);
  return isNaN(ms) ? null : ms;
}

/** Renders an epoch-ms x-axis value as a short human date, for chart ticks/tooltips/tables/sonification. */
export function formatDateAxisValue(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Epoch ms -> `yyyy-mm-dd`, for round-tripping through a native `<input type="date">`. */
export function msToIsoDateInput(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** `yyyy-mm-dd` (from a native date input) -> epoch ms at UTC midnight, or `null` if empty/invalid.
 * Requires a strict `yyyy-mm-dd` match before ever calling `Date.parse` — this is also called on a
 * row's raw x value when switching the x-axis type back to 'date', which may still hold whatever
 * was preserved from a different type (e.g. a plain number like "5"), not a real date-input value.
 * Without this guard, `Date.parse("5T00:00:00Z")` doesn't reliably return NaN for a non-date string
 * the way `parseDateValue` explicitly checks for — it can silently coerce into a bogus timestamp
 * instead of being rejected, corrupting the row instead of leaving it untouched. */
export function isoDateInputToMs(iso: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const ms = Date.parse(`${iso}T00:00:00Z`);
  return isNaN(ms) ? null : ms;
}

/**
 * The y-axis bounds the chart DRAWS, computed rather than left to Recharts.
 *
 * Why this exists at all: the announcement has to state the range a sighted student reads off the
 * axis, and it was stating the data's own max instead — so an axis topping out at 1.8 was announced
 * as 1.62. There is no way to ask Recharts what it chose, and reimplementing its guess would be a
 * second algorithm free to disagree with the first. So the domain becomes ours, passed explicitly
 * to `<YAxis>` and read by the sonification player, and the two cannot drift because they are the
 * same numbers.
 *
 * `include` pulls values into view that must be on the axis whatever the data does. Two of those:
 * a reference line drawn outside the domain is a line the chart clips away, and a BAR chart has to
 * reach zero or its bars lie — a series spanning 100 to 110 drawn from 100 makes a 10% difference
 * look total. Recharts forced zero for bars on its own; taking the domain over means taking that
 * responsibility with it.
 *
 * Note this CHANGES what is drawn. Recharts' own choice was undocumented and could shift under a
 * version bump; this one is a conventional 1/2/5 nice scale aiming for about five ticks.
 */
const TARGET_TICKS = 5;

function niceStep(raw: number): number {
  if (!(raw > 0)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

/**
 * The domain AND the ticks that go on it.
 *
 * Both, from one call, because sizing the axis needs the tick TEXT and not the data's magnitude.
 * The width heuristic beside this used to measure `Math.ceil(max)` — the integer part of the
 * largest value — which was close enough while Recharts chose its own ticks and wrong the moment
 * the domain became ours: a 0.5 step renders "-0.5", four characters in the space reserved for one.
 * The y-axis label then crowded the numbers.
 */
export function niceAxisScale(values: number[], ...include: Array<number | undefined>): { domain: [number, number]; ticks: number[] } {
  const domain = niceAxisDomain(values, ...include);
  const [lo, hi] = domain;
  const step = niceStep((hi - lo) / TARGET_TICKS);
  const ticks: number[] = [];
  // `<=` with a half-step of slack: the bounds are already snapped to multiples of the step, but
  // accumulating by addition drifts, and a missing final tick is a visibly lopsided axis.
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(parseFloat(v.toPrecision(12)));
  return { domain, ticks };
}

export function niceAxisDomain(values: number[], ...include: Array<number | undefined>): [number, number] {
  const finite = values.filter((v) => Number.isFinite(v));
  for (const extra of include) {
    if (extra !== undefined && Number.isFinite(extra)) finite.push(extra);
  }
  if (!finite.length) return [0, 1];
  let lo = Math.min(...finite);
  let hi = Math.max(...finite);
  // A flat series has no span to divide; give it one so the axis is not a single line.
  if (lo === hi) {
    const pad = Math.abs(lo) || 1;
    lo -= pad;
    hi += pad;
  }
  const step = niceStep((hi - lo) / TARGET_TICKS);
  // `toPrecision` because the multiply reintroduces binary error the floor/ceil just removed —
  // 0.5 * 4 lands on 2.0000000000000004, which would be announced and drawn with the residue.
  const snap = (v: number) => parseFloat(v.toPrecision(12));
  return [snap(Math.floor(lo / step) * step), snap(Math.ceil(hi / step) * step)];
}
